import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from "@/Components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';

const VerifyEmail = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                const response = await axiosInstance.post('/auth/verify-email', { token });
                if (response.data.success) {
                    setVerificationStatus('success');  
                } else {
                    setVerificationStatus('error');
                    setErrorMessage(response.data.error);
                }
            } catch (error) {
                console.error('Verification error:', error);
                setVerificationStatus('error');
                if (error.response?.data?.error) {
                    setErrorMessage(error.response.data.error);
                } else {
                    setErrorMessage(t('verification.verificationError') || 'Er is een fout opgetreden bij het verifieren van je e-mailadres.');
                }
            }
        };

        if (token) {
            verifyEmail();
        } else {
            setVerificationStatus('error');
            setErrorMessage(t('verification.noTokenFound') || 'Geen verificatie token gevonden.');
        }
    }, [token, t]);

    const getContent = () => {
        switch (verificationStatus) {
            case 'verifying':
                return (
                    <div className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin text-ps-blue-dark mx-auto mb-4" />
                        <h2 className="text-2xl font-inter text-ps-blue-dark mb-2">
                            {t('verification.verifyingEmail')}
                        </h2>
                        <p className="text-gray-600 font-inter">
                            {t('verification.pleaseWait')}
                        </p>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center">
                        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-inter text-ps-blue-dark mb-2">
                            {t('verification.emailVerified')}
                        </h2>
                        <p className="text-gray-600 font-inter mb-6">
                            {t('verification.emailVerifiedSuccess')}<br /><br />{t('verification.approvalNeeded')}
                        </p>
                        <Button
                            onClick={() => navigate('/')}
                            className="bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white font-inter"
                        >
                            {t('verification.goToLogin')}
                        </Button>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center">
                        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-inter text-ps-blue-dark mb-2">
                            {t('verification.verificationFailed')}
                        </h2>
                        <p className="text-red-600 font-inter mb-6">
                            {errorMessage}
                        </p>
                        <div className="space-y-4">
                            <Button
                                onClick={() => navigate('/')}
                                className="bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white font-inter"
                            >
                                {t('verification.goToLogin')}
                            </Button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12">
            <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
                {getContent()}
            </div>
        </div>
    );
};

export default VerifyEmail;